// ============================================================
// ERROR BOUNDARY — catches render errors below this point
// ============================================================
// React error boundaries are class-only (no hook equivalent yet).
// We use two layers in main.jsx + App.jsx:
//   1. App-level — catches catastrophic failures (white screen)
//   2. Route-level — catches a page crash without nuking the nav/sidebar
//
// On error: shows a calm fallback + a "Reload" button + an event id
// the customer can quote to support. Reports to Sentry if configured.

import { Component } from 'react';
import { captureError } from '../lib/sentry.js';
import { BRAND, NETCOM_LOCKUP } from '../config/brand.js';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, eventId: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Report to Sentry. errorInfo.componentStack is the React component stack.
    try {
      captureError(error, {
        componentStack: errorInfo?.componentStack,
        scope: this.props.scope || 'unknown',
      });
    } catch {
      // Sentry itself failed — don't make a bad day worse
    }
    if (typeof this.props.onError === 'function') {
      this.props.onError(error, errorInfo);
    }
  }

  handleReload = () => {
    // Hard reload — clears stale module cache + ServiceWorker fetch graph
    window.location.reload();
  };

  handleHome = () => {
    window.location.assign('/');
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    // Caller can pass a custom fallback for inline boundaries
    if (this.props.fallback) {
      return typeof this.props.fallback === 'function'
        ? this.props.fallback({ error: this.state.error, reload: this.handleReload })
        : this.props.fallback;
    }

    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6 bg-slate-50">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg shadow-sm p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-red-50 text-red-600 mx-auto mb-4 flex items-center justify-center text-xl">
            !
          </div>
          <h1 className="text-lg font-semibold text-slate-900 mb-2">
            Something went wrong on this page
          </h1>
          <p className="text-sm text-slate-600 mb-6">
            We've logged the issue. Try reloading — if it keeps happening, contact
            support and we'll dig in.
          </p>
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              onClick={this.handleReload}
              className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
            >
              Reload page
            </button>
            <button
              type="button"
              onClick={this.handleHome}
              className="px-4 py-2 rounded-md border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
            >
              Back to dashboard
            </button>
          </div>
          {import.meta.env?.DEV && this.state.error ? (
            <pre className="mt-6 text-left text-xs text-red-700 bg-red-50 border border-red-200 rounded p-3 overflow-auto max-h-48">
              {String(this.state.error?.stack || this.state.error)}
            </pre>
          ) : null}
          <p className="mt-6 text-[11px] text-slate-400">
            {BRAND.company} · {NETCOM_LOCKUP}
          </p>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
